import { API_BASE } from '../config';

export const RECENT_ASSIGNABLE_PROJECTS_LIMIT = 8;

export interface AssignableProject {
  id: string;
  title: string;
  updatedAt: string;
}

export function canEditSharedProjectPermission(permission: string): boolean {
  return permission === 'editor' || permission === 'admin';
}

export function selectRecentAssignableProjects(
  projects: AssignableProject[],
  limit: number = RECENT_ASSIGNABLE_PROJECTS_LIMIT
): AssignableProject[] {
  return [...projects]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, Math.max(0, limit));
}

export async function fetchAssignableProjects(token: string): Promise<AssignableProject[]> {
  const [ownedRes, sharedRes] = await Promise.all([
    fetch(`${API_BASE}/api/projects`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
    fetch(`${API_BASE}/api/shared-projects`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  ]);

  const owned: AssignableProject[] = [];
  if (ownedRes.ok) {
    const data = await ownedRes.json();
    for (const project of data.projects || []) {
      owned.push({
        id: project.id,
        title: project.title,
        updatedAt: project.updatedAt,
      });
    }
  }

  const shared: AssignableProject[] = [];
  if (sharedRes.ok) {
    const data = await sharedRes.json();
    for (const item of data.projects || []) {
      const permission = item?.share?.permission;
      if (!canEditSharedProjectPermission(permission)) {
        continue;
      }
      const project = item.project;
      if (!project?.id) {
        continue;
      }
      shared.push({
        id: project.id,
        title: project.title,
        updatedAt: project.updatedAt,
      });
    }
  }

  const byId = new Map<string, AssignableProject>();
  for (const project of [...owned, ...shared]) {
    const existing = byId.get(project.id);
    if (!existing || new Date(project.updatedAt).getTime() > new Date(existing.updatedAt).getTime()) {
      byId.set(project.id, project);
    }
  }

  return selectRecentAssignableProjects(Array.from(byId.values()), Number.POSITIVE_INFINITY);
}
